import { COVER_CONSTANTS } from "@/features/survey/components/coverSection/constants";
import { EmeraldCheckbox } from "@emerald-react/checkbox";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldSelect } from "@emerald-react/select";
import useDidUpdateEffect from "@hooks/useDidUpdateEffect";
import useSurveyState from "@hooks/useSurveyState";
import useSurveyUpdate from "@hooks/useSurveyUpdate";
import {
  useGetSurveyCoverImageQuery,
  useGetSurveyMediaFileQuery,
} from "@services/media";
import Analytics from "@shared/utils/tagManagerAnalytics";
import ClientLogoSection from "@survey-cover/ClientLogoSection";
import ContactList from "@survey-cover/ContactList";
import { updateCurrentSurvey } from "@survey/surveySlice";
import { useUpdateSurveyFlagsMutation } from "@services/survey-core";
import { type BaseSyntheticEvent, useState } from "react";

const CoverSection = () => {
  const { survey, dispatch, updateSurvey } = useSurveyState();
  const [updateSurveyFlags] = useUpdateSurveyFlagsMutation();
  const currentYear = COVER_CONSTANTS.CURRENT_YEAR;

  // Use simplified survey update hooks for form fields
  const title = useSurveyUpdate("coverTitle", survey?.coverTitle || "");
  const subtitle = useSurveyUpdate(
    "coverSubTitle",
    survey?.coverSubTitle || "",
  );
  const date = useSurveyUpdate("coverDate", survey?.coverDate || "");
  const clientName = useSurveyUpdate("clientName", survey?.clientName || "");

  const { data: cbreMediaResponse, isSuccess: isCbreMediaSuccess } =
    useGetSurveyCoverImageQuery({ mediaType: "SURVEYCOVERIMAGE" });

  const coverBackgroundImages =
    !isCbreMediaSuccess || !cbreMediaResponse
      ? []
      : [...(cbreMediaResponse.media || [])].sort((a, b) =>
        (a.displayName || "").localeCompare(b.displayName || ""),
      );

  const { data: teamMediaResponse, isSuccess: isTeamMediaSuccess } =
    useGetSurveyMediaFileQuery(
      { mediaType: "TEAMCOVERIMAGE", surveyId: survey?.id },
      { skip: !survey?.id },
    );

  const teamCoverImages =
    !isTeamMediaSuccess || !teamMediaResponse
      ? []
      : [...(teamMediaResponse?.media || [])].sort((a, b) =>
        (a.displayName || "").localeCompare(b.displayName || ""),
      );

  const [backgroundImage, setBackgroundImage] = useState(
    survey?.coverBackgroundImage.mediaId,
  );

  const handleCheckboxChange = (name: string) => (checked: boolean) => {
    if (survey) {
      updateSurveyFlags({ id: survey.id, [name]: checked });
    }
  };

  const handleBackgroundImageChange = (
    _event: BaseSyntheticEvent,
    value: string | string[],
  ) => {
    if (survey?.coverBackgroundImage?.displayName) {
      Analytics.send({
        SurveyCoverImageChanged: survey.coverBackgroundImage.displayName,
      });
    }
    setBackgroundImage(Array.isArray(value) ? value[0] : value);
  };

  // Update survey.coverBackgroundImage when backgroundImage changes
  useDidUpdateEffect(() => {
    if (
      survey &&
      backgroundImage &&
      backgroundImage !== "team-heading" &&
      backgroundImage !== "cbre-heading"
    ) {
      const selectedMedia =
        teamCoverImages.find((media) => media.mediaId === backgroundImage) ||
        coverBackgroundImages.find(
          (media) => media.mediaId === backgroundImage,
        );
      if (
        selectedMedia &&
        survey?.coverBackgroundImage?.mediaId !== selectedMedia?.mediaId
      ) {
        const coverBackgroundImageData = {
          mediaId: selectedMedia.mediaId,
          s3Url: selectedMedia.s3Url || "",
          displayName: selectedMedia.displayName || "",
          cloudfrontUrl: selectedMedia.cloudfrontUrl || "",
        };
        // Update local state first
        dispatch(
          updateCurrentSurvey({
            coverBackgroundImage: coverBackgroundImageData,
          }),
        );
        // Then make API call
        updateSurvey({
          id: survey.id,
          coverBackgroundImage: coverBackgroundImageData,
        });
      }
    }
  }, [backgroundImage]);

  const options = [
    ...(teamCoverImages.length > 0
      ? [
        {
          label: "Team Cover Images" as const,
          value: "team-heading" as const,
          disabled: true as const,
        },
      ]
      : []),
    ...teamCoverImages.map((media) => ({
      value: media.mediaId,
      label: media.displayName || "Untitled Image",
    })),
    ...(coverBackgroundImages.length > 0
      ? [{ label: "CBRE Cover Images", value: "cbre-heading", disabled: true }]
      : []),
    ...coverBackgroundImages.map((media) => ({
      value: media.mediaId,
      label: media.displayName || "Untitled Image",
    })),
  ];

  return (
    <section className="flex flex-col gap-y-4 w-full">
      <EmeraldSelect
        name="coverBackgroundImage"
        id="SC-SurveyCoverImage"
        label="Background Image"
        value={backgroundImage}
        onChange={handleBackgroundImageChange}
        options={options}
        className="max-h-64!"
        searchable
      />
      <EmeraldInput
        id="SC-SurveyTitle"
        label="Survey Title"
        type="text"
        value={title.value}
        onChange={(e: BaseSyntheticEvent) => title.setValue(e.target.value)}
        onBlur={title.triggerSave}
        placeholder="Survey Title"
      />
      <EmeraldInput
        id="SC-SurveySubtitle"
        label="Survey Subtitle"
        type="text"
        value={subtitle.value}
        onChange={(e: BaseSyntheticEvent) => subtitle.setValue(e.target.value)}
        onBlur={subtitle.triggerSave}
        placeholder="Survey Subtitle"
      />
      <EmeraldInput
        id="SC-SurveyDate"
        label="Date"
        type="text"
        value={date.value}
        onChange={(e: BaseSyntheticEvent) => date.setValue(e.target.value)}
        onBlur={date.triggerSave}
        placeholder={`January 1, ${currentYear}`}
      />
      <EmeraldInput
        id="SC-SurveyClientName"
        label="Client Name"
        type="text"
        value={clientName.value}
        onChange={(e: BaseSyntheticEvent) =>
          clientName.setValue(e.target.value)
        }
        onBlur={clientName.triggerSave}
        placeholder="Client Name"
      />
      <EmeraldCheckbox
        id="displayClientName SC-ClientNameShowHide"
        checked={survey?.isDisplayClientName || false}
        label="Display Client Name"
        className="emerald-checkbox text-emerald-900 font-medium text-base"
        onChange={handleCheckboxChange("isDisplayClientName")}
      />

      <ClientLogoSection />
      <ContactList />
    </section>
  );
};

// Add displayName for better debugging
CoverSection.displayName = "CoverSection";

export default CoverSection;
